import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { useRowScenarioInfo } from '../hooks'
import { formatDateForLocale } from '@/lib/dateUtils'

function normalizeDate(d: string | Date | null | undefined): string | null {
    if (!d) return null
    if (d instanceof Date) return d.toISOString()
    if (typeof d === 'string') return d.includes('T') ? d : d.replace(' ', 'T') + (d.includes('Z') || /[+-]\d{2}:\d{2}$/.test(d) ? '' : 'Z')
    return null
}

interface ScenarioInfoDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    requestID: string | null
    scenario: number | null
    objectID?: string | null
    objectDescription?: string | null
}

export function ScenarioInfoDialog({
    open,
    onOpenChange,
    requestID,
    scenario,
    objectID = null,
    objectDescription = null,
}: ScenarioInfoDialogProps) {
    const { t, i18n } = useTranslation()
    const { data, isLoading } = useRowScenarioInfo(requestID, scenario, open)

    const locale = i18n.language || 'en-US'

    const requestSetLines = (data?.requestSets ?? []).map((row) => {
        const obj = row.objectID ?? ''
        const dateStr = normalizeDate(row.requestDate)
        const formatted = dateStr ? formatDateForLocale(dateStr, locale, { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }) : ''
        return `Part of ${obj}, ${formatted}`.trimEnd()
    })

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle title={t('recoverRequests.scenarioAndRequestInfo', 'Scenario and Request Information')}>
                        {t('recoverRequests.scenarioAndRequestInfo', 'Scenario and Request Information')}
                    </DialogTitle>
                    <DialogDescription className="sr-only">
                        {t('recoverRequests.scenarioInfoDescription', 'Scenario name, description, use case and request sets for this recovery request.')}
                    </DialogDescription>
                </DialogHeader>
                {(objectID != null && objectID !== '') || (objectDescription != null && objectDescription !== '') ? (
                    <div className="space-y-1 text-sm border-b pb-3">
                        {objectID != null && objectID !== '' && (
                            <p><span className="font-medium">{t('recoverRequests.objectID', 'Object ID')}:</span> {objectID}</p>
                        )}
                        {objectDescription != null && objectDescription !== '' && (
                            <p><span className="font-medium">{t('recoverRequests.objectDescription', 'Object Description')}:</span> {objectDescription}</p>
                        )}
                    </div>
                ) : null}
                {isLoading && <p className="text-muted-foreground text-sm">{t('common.loading', 'Loading...')}</p>}
                {!isLoading && data && (
                    <div className="space-y-4 text-sm">
                        {data.scenario && (
                            <div className="space-y-2">
                                <h4 className="font-medium">{data.scenario.scenarioName ?? '-'}</h4>
                                {data.scenario.description != null && data.scenario.description !== '' && (
                                    <p className="text-muted-foreground">{data.scenario.description}</p>
                                )}
                                {data.scenario.useCase != null && data.scenario.useCase !== '' && (
                                    <p><span className="font-medium">{t('recoverRequests.useCase', 'Use case')}:</span> {data.scenario.useCase}</p>
                                )}
                            </div>
                        )}
                        {!data.scenario && <p className="text-muted-foreground">{t('recoverRequests.noScenario', 'No scenario information.')}</p>}
                        {requestSetLines.length > 0 && (
                            <div>
                                <h4 className="font-medium mb-2">{t('recoverRequests.requestSets', 'Request sets')}</h4>
                                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                                    {requestSetLines.map((line, i) => (
                                        <li key={i}>{line}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    )
}
